import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {getEmailStatusBadgeState} from "@mytour/shared";
import {useEffect, useState} from "react";
import {Button, Spinner} from "reactstrap";
import {CertificateRecapModal} from "../recap/CertificateRecapModal";
import type {CertificateEventProps} from "../types";
import {CertificateWizardModal} from "../wizard/CertificateWizardModal";
import {CertificateReadError} from "./CertificateReadError";
import {useCertificateAction} from "./useCertificateAction";

type Props = CertificateEventProps;

export function CertificateAction({
  event,
  customer,
  disabled,
  onPerformed,
  customerQueries,
}: Props) {
  const {
    isLoading,
    hasCertificate,
    certificate,
    needsCompletion,
    latestCertificate,
    company,
    platformUserStyle,
    intervenantName,
    isPending,
    showModal,
    openModal,
    closeModal,
    handleSave,
    handleResume,
    saveError,
    certificateError,
    isCertificateFetching,
    refetchCertificate,
  } = useCertificateAction(event, customer, onPerformed);

  const [resendModalEventId, setResendModalEventId] = useState<string | null>(null);
  const isResendModalOpen =
    !!event.id && resendModalEventId === event.id && !!certificate?.hasFile;
  const badgeState = getEmailStatusBadgeState(certificate?.emailStatus);

  useEffect(() => {
    setResendModalEventId((openedEventId) => (openedEventId === event.id ? openedEventId : null));
  }, [event.id]);

  if (certificateError && !isResendModalOpen) {
    return (
      <CertificateReadError
        eventId={event.id}
        disabled={disabled || isPending}
        isFetching={isCertificateFetching}
        onRetry={refetchCertificate}
      />
    );
  }

  if (isLoading) {
    return null;
  }

  return (
    <>
      {needsCompletion ? (
        <Button color="warning" outline disabled={disabled || isPending} onClick={handleResume}>
          {isPending ? <Spinner size="sm" /> : <FontAwesomeIcon icon="file-pdf" />}
          &nbsp; Reprendre le PDF
        </Button>
      ) : hasCertificate ? (
        <Button
          color="success"
          outline
          disabled={disabled || isPending}
          onClick={handleOpenResend}
        >
          <FontAwesomeIcon icon="file-pdf" />
          &nbsp; Voir l'attestation
        </Button>
      ) : (
        <Button color="success" disabled={disabled || isPending} onClick={openModal}>
          {isPending ? <Spinner size="sm" /> : <FontAwesomeIcon icon="plus" />}
          &nbsp; Créer attestation de ramonage
        </Button>
      )}
      {certificate?.hasFile && badgeState && (
        <Button
          className="ms-2"
          color="success"
          outline
          disabled={disabled || isPending}
          onClick={handleOpenResend}
        >
          <FontAwesomeIcon icon="envelope" />
          &nbsp;{" "}
          {badgeState === "uncertain"
            ? "Envoi à vérifier"
            : badgeState === "failed"
              ? "Renvoyer par email"
              : "Envoyer par email"}
        </Button>
      )}
      {isResendModalOpen && event.id && (
        <CertificateRecapModal
          customerQueries={customerQueries}
          key={event.id}
          eventId={event.id}
          customerId={customer.id}
          onClose={handleCloseResend}
        />
      )}
      {showModal && company && platformUserStyle && (
        <CertificateWizardModal
          customerQueries={customerQueries}
          company={company}
          platformUserStyle={platformUserStyle}
          defaultValues={latestCertificate ?? undefined}
          customer={customer}
          intervenantName={intervenantName}
          error={saveError}
          onSave={handleSave}
          onClose={closeModal}
        />
      )}
    </>
  );

  function handleOpenResend() {
    setResendModalEventId(event.id ?? null);
  }

  function handleCloseResend() {
    setResendModalEventId(null);
  }
}
